import { Resolution } from '../data/Resolution';
import Layer from '../data/Layer';
import { MotionSource } from '../motion/MotionSource';
import PixelRatio from '../data/PixelRatio';
import FlyerType from '../data/FlyerType';
import {
  FlyerData,
  TemplateImageContentMode,
  TemplateUI,
  TemplateUIIntroType,
  TemplateUIType
} from '../data/FlyerData';
import { Soundtrack } from '../audio/Soundtrack';
import { Frame } from '../data/Frame';
import SaveState from './SaveState';
import Storable from '../data/Storable';
import { MuteMotionSource } from '../motion/MuteMotionSource';
import { PictureFrame } from '../data/PictureFrame';
import { Effect } from '@beatflyer/shaders-lib';
import slugify from 'slugify';
import { CompositionEffect } from 'compose/CompositionEffect';
import { CaptionsToken } from 'data/CaptionsToken';
import { Command } from './commands/Command';
import { PropertyTimeline } from 'data/LayerTimeline';
import { FrameType } from 'data/FrameType';
import { getCloneBaseId } from 'utils';

export default interface FlyerState {
  id: string | null | undefined;
  name: string | null;
  type: FlyerType;
  layers: Layer[];
  motionSource: MotionSource;
  muteMotionSources: MuteMotionSource[];
  soundtrack: Soundtrack | null;
  isPlaying: boolean;
  isMuted: boolean;
  shouldPlay: boolean;
  previewedEffect: Effect | CompositionEffect | null;
  resolution: Resolution;
  previewPixelRatio: PixelRatio;
  hasNewChanges: boolean;
  saveStatus: SaveState;
  removedAssets: string[];
  lastSave: number;
  lastTimelinesValues?: string;
  lastTimeMotionSourceValues?: string;
  maxMutedDuration: number;
  originTemplateId: string | null;
  templateUIIntro: TemplateUIIntroType;
  captions: CaptionsToken[] | null;
  captionsLanguageCode: string | null;
  // UNDO
  pastCommands: Command[];
  // presentCommand: Command | null;
  futureCommands: Command[];
}

export const flyerStateToData = (
  state: FlyerState,
  forDatabase: boolean = false
): FlyerData => {
  const {
    resolution,
    name,
    layers,
    soundtrack,
    motionSource,
    captions,
    captionsLanguageCode
  } = state;
  const result: FlyerData = {
    layers: [],
    resolution,
    captions,
    captionsLanguageCode
  };
  if (forDatabase && name) {
    result.name = name;
  }
  layers!.forEach(layer => {
    result.layers!.push(layer.toObject(forDatabase));
  });
  if (soundtrack) {
    if (soundtrack.url) {
      result.audioUrl = soundtrack.url;
    }
    if (soundtrack.thumbUrl) {
      result.audioThumbUrl = soundtrack.thumbUrl;
    }
    if (soundtrack.title) {
      result.audioTitle = soundtrack.title;
    }
    if (soundtrack.artist) {
      result.audioArtist = soundtrack.artist;
    }
    // if (soundtrack.audioFadeLength > 0) {
    //   result.audioFadeLength = soundtrack.audioFadeLength;
    // }
  }
  if (motionSource !== soundtrack) {
    result.motionSource = (state.motionSource as MuteMotionSource).asData();
  }
  if (state.templateUIIntro !== TemplateUIIntroType.None) {
    result.templateUIIntro = state.templateUIIntro;
  }
  result.length = soundtrack?.getDuration() || motionSource.getDuration();
  return result;
};

export const flyerContainsImage = (
  state: FlyerState,
  imageName: string
): boolean => {
  for (let i = 0; i < state.layers.length; i++) {
    const layer: Layer = state.layers[i];
    for (let j = 0; j < layer.frames.length; j++) {
      const frame: Frame = layer.frames[j];
      if (
        frame instanceof PictureFrame &&
        (frame as PictureFrame).filename === imageName
      ) {
        return true;
      }
    }
  }
  return false;
};

export const getAllStorableAssets = (state: FlyerState): Storable[] => {
  const result: Storable[] = [];
  state.layers.forEach(layer => {
    layer.frames.forEach(frame => {
      if (frame instanceof PictureFrame) {
        result.push(frame);
      }
    });
  });
  if (state.soundtrack) {
    result.push(state.soundtrack);
  }
  return result;
};

export const getExportedFileName = (state: FlyerState): string => {
  if (state.name) {
    return slugify(state.name, { lower: true });
  } else if (state.id) {
    return state.id;
  } else {
    return 'flyer';
  }
};

export const getLayerById = (
  state: FlyerState,
  layerId: string
): Layer | undefined => {
  const { layers } = state;
  for (const layer of layers) {
    if (layer.id === layerId) {
      return layer;
    }
  }
  console.log('getLayerById', 'Could not find layer with id', layerId);
  return undefined;
};

export const getFrameById = (
  state: FlyerState,
  frameId: string
): Frame | undefined => {
  const { layers } = state;
  for (const layer of layers) {
    for (const frame of layer.frames) {
      if (frame.id === frameId) {
        return frame;
      }
    }
  }
  console.log('getFrameById', 'Could not find frame with id', frameId);
  return undefined;
};

export const getLayerOrFrameById = (
  state: FlyerState,
  id: string
): Layer | Frame | undefined => {
  const { layers } = state;
  for (const layer of layers) {
    if (layer.id === id) {
      return layer;
    }
    for (const frame of layer.frames) {
      if (frame.id === id) {
        return frame;
      }
    }
  }
  console.log(
    'getLayerOrFrameById',
    'Could not find neither layer nor frame with id',
    id
  );
  return undefined;
};

export const getEffectById = (
  state: FlyerState,
  effectId: string
): Effect | CompositionEffect | undefined => {
  const { layers } = state;
  for (const layer of layers) {
    if (layer.compositionEffect && layer.compositionEffect.id === effectId) {
      return layer.compositionEffect;
    }
    for (const effect of layer.effects) {
      if (effect.id === effectId) {
        return effect;
      }
    }
  }
  console.log('getEffectById', 'Could not find effect with id', effectId);
  return undefined;
};

export const getTimelineValues = (state: FlyerState): string => {
  const data: any = {};
  state.layers.forEach(layer => {
    const layerData: any = {};
    const keys = Object.keys(layer.timeline);
    for (const k of keys) {
      const timeline = layer.timeline[k];
      layerData[k] = timeline.asData();
    }
    data[layer.id] = layerData;
  });
  return JSON.stringify(data);
};

export const setTimelineValues = (state: FlyerState, values: string) => {
  const data = JSON.parse(values);
  state.layers.forEach(layer => {
    const layerData = data[layer.id];
    if (layerData) {
      const timeline: any = {};
      const keys = Object.keys(layerData);
      for (const k of keys) {
        timeline[k] = PropertyTimeline.createFromData(layerData[k]);
      }
      layer.timeline = timeline;
    }
  });
};

const getFramesByType = (state: FlyerState, type: FrameType): Frame[] => {
  const result: Frame[] = [];
  state.layers.forEach(layer => {
    layer.frames.forEach(f => {
      if (f.type === type) {
        result.push(f);
      }
    });
  });
  return result;
};

export const flyerCanBeEditedOnMobile = (state: FlyerState) => {
  const textOrPictureFrames: Frame[] = [];
  state.layers.forEach(l => {
    l.frames.forEach(f => {
      if (f.type === FrameType.Image || f.type === FrameType.Text) {
        textOrPictureFrames.push(f);
      }
    });
  });
  return textOrPictureFrames.length > 0;
};

export const generateTemplateUI = (state: FlyerState): TemplateUI[] | null => {
  const result: TemplateUI[] = [];
  getFramesByType(state, FrameType.Text).forEach((f, index) => {
    result.push({
      label: `Text ${index + 1}`,
      type: TemplateUIType.Text,
      bind: `${f.id}@text`
    });
  });
  getFramesByType(state, FrameType.Image).forEach((f, index) => {
    const { x, y, width, height } = f.boundingBox;
    result.push({
      label: `Image ${index + 1}`,
      type: TemplateUIType.Image,
      bind: [
        {
          node: `${f.id}@url`,
          options: {
            rect: {
              x,
              y,
              width,
              height
            },
            contentMode: TemplateImageContentMode.Fit
          }
        }
      ]
    });
  });
  if (result.length === 0) {
    return null;
  }
  return result;
};

export const getLayerCopyIndex = (
  layerId: string,
  state: FlyerState
): number => {
  const baseLayerId = getCloneBaseId(layerId);
  // find next available index
  let index = 1;
  while (getLayerById(state, `${baseLayerId}-${index}`) !== undefined) {
    index++;
  }
  return index;
};



// WEBPACK FOOTER //
// ./src/state/FlyerState.ts